/**
 * Where a document came from and what came with it: the email it arrived in, and the files of the
 * same transaction that are attached to it (the receipt next to its invoice). Those files are part
 * of the Beleg and kept with it, but booked only once - as this document. For a photographed
 * receipt, also the photos as taken: the file shown is the straightened scan made from them.
 */
import { useQuery } from "@tanstack/react-query";
import { Camera, Mail, Paperclip } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { pb } from "@/lib/platform/pocketbase";
import type { DocumentRecord } from "@/lib/api/documents";
import { formatDate } from "@/lib/utils/format";

/** PocketBase stores "receipt.pdf" as "receipt_k3j2h1g9f8.pdf"; show the name the sender used. */
export function displayName(stored: string): string {
  return stored.replace(/_[a-z0-9]{10}(?=\.[^.]+$)/i, "");
}

export function DocumentOrigin({ document }: { document: DocumentRecord }) {
  const { t } = useTranslation();
  const attachments = Array.isArray(document.attachments) ? document.attachments : [];
  const originals = Array.isArray(document.originals) ? document.originals : [];
  const message = document.expand?.inboundMessage;

  const { data: token } = useQuery({
    queryKey: ["file-token", document.id],
    queryFn: () => pb.files.getToken(),
    enabled: attachments.length + originals.length > 0,
    staleTime: 60_000,
  });

  if (document.source !== "email" && attachments.length === 0 && originals.length === 0)
    return null;
  const href = (name: string) => (token ? pb.files.getURL(document, name, { token }) : undefined);

  return (
    <div className="space-y-3 rounded-lg border p-4 text-sm" data-testid="document-origin">
      {document.source === "email" && (
        <div className="flex gap-2">
          <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <p>
              {message
                ? t("document.receivedByEmail", {
                    sender: message.sender,
                    date: formatDate(message.receivedAt),
                  })
                : t("document.receivedByEmailShort")}
            </p>
            {message?.subject && (
              <p className="truncate text-xs text-muted-foreground">{message.subject}</p>
            )}
            <Link
              to="/mailbox"
              className="text-xs text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
            >
              {t("document.toMailbox")}
            </Link>
          </div>
        </div>
      )}
      {originals.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {t("document.originals", { count: originals.length })}
          </p>
          <ul className="space-y-1" data-testid="document-originals">
            {originals.map((name) => (
              <li key={name} className="flex items-center gap-2">
                <Camera className="size-3.5 shrink-0 text-muted-foreground" />
                <a
                  href={href(name)}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate underline-offset-2 hover:underline"
                >
                  {displayName(name)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {attachments.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {t("document.attachments", { count: attachments.length })}
          </p>
          <ul className="space-y-1" data-testid="document-attachments">
            {attachments.map((name) => (
              <li key={name} className="flex items-center gap-2">
                <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
                <a
                  href={href(name)}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate underline-offset-2 hover:underline"
                >
                  {displayName(name)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
